import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  WarningConfirmationModal as DocumentWarningConfirmationModal,
  FormController,
  MuiBox,
  MuiDateField,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  WarningConfirmationModal,
} from "../../../../../components";
import { MuiLink } from "../../../../../components/link/MuiLink";
import {
  BE_ERROR_CODES,
  DOCUMENT_SITE_CORE_KEY,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  ClientDocument,
  CreateClientDocumentsFormKey,
  DocumentsProps,
} from "../../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

import { useDispatch, useSelector } from "react-redux";
import {
  useArchiveStatusContractDocumentMutation,
  useDeleteContractDocumentMutation,
  useGetContractClientDocument,
  useUnmapContractDocumentMutation,
  useUpdateStatusContractDocumentMutation,
} from "../../../../../api/services/client/clientApis";
import {
  ArchiveStatusOtherDocumentParams,
  UpdateStatusContractDocumentParams,
} from "../../../../../api/services/client/models";
import MuiChipWithPopover from "../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../../components/statusBadge/StatusBadge";
import { COMPONENT_IDS } from "../../../../../helpers/constants/componentIds";
import {
  arrayOfObjectsIncludesValue,
  convertToUserFormat,
  getParsedErrorMessage,
  getValueOrBlank,
  isEmpty,
  isErrorCode,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../../redux/slices/client/createClientSelector";
import ArchiveContractDocumentModal from "../createClientOtherDocumentList/archiveContractDocumentModal/ArchiveContractDocumentModal";
import CancelContractDocumentModal from "./cancelContractDocumentModal/CancelContractDocumentModal";
import { DocumentListWrapper } from "./DocumentList.style";
import { OptionMenu } from "./optionMenu/OptionMenu";

export const pageId = "create-client-document-list";
const ACTION_CANCEL_CONTRACT = "cancel_contract";
const ACTION_ACTIVE = "active";
const ACTION_EXPIRED = "expired";
const ACTION_ARCHIVE = "archive";

const PARAMS_CANCEL_CONTRACT = "Cancelled";
const PARAMS_UNDO_CANCEL_CONTRACT = "UndoCancel";

export const DocumentList: FC<DocumentsProps> = ({
  rows,
  onDocumentView,
  onDocumentEdit,
  successHandler,
  isEditable = true,
}) => {
  const selectedDocumentRef = useRef<ClientDocument>();
  const gridRef = createRef<HTMLDivElement>();
  const selectedDocumentActionRef = useRef<string>(ACTION_ACTIVE);
  const {
    mutateAsync: deleteContractDocument,
    isPending: isDeleteContractDocumentPending,
    isSuccess: isDeleteContractDocumentSuccess,
    isError: isDeleteContractDocumentError,
    error: deleteContractDocumentError,
  } = useDeleteContractDocumentMutation();

  const {
    mutateAsync: updateStatusContractDocument,
    isPending: isUpdateStatusContractDocumentPending,
    isSuccess: isUpdateStatusContractDocumentSuccess,
    isError: isUpdateStatusContractDocumentError,
    error: updateStatusContractDocumentError,
  } = useUpdateStatusContractDocumentMutation();

  const {
    mutateAsync: archiveStatusContractDocument,
    isPending: isArchiveStatusContractDocumentPending,
    isSuccess: isArchiveStatusContractDocumentSuccess,
    isError: isArchiveStatusContractDocumentError,
    error: archiveStatusContractDocumentError,
  } = useArchiveStatusContractDocumentMutation();

  const {
    mutateAsync: getContractClientDocument,
    data: contractClientDocumentData,
    isSuccess: isContractClientDocumentSuccess,
  } = useGetContractClientDocument();

  const {
    mutateAsync: unmapContractDocumentsMutation,
    isPending: isUnmapContractDocumentPending,
    isSuccess: isUnmapContractDocumentSuccess,
    isError: isUnmapContractDocumentError,
    error: unmapContractDocumentError,
  } = useUnmapContractDocumentMutation();

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { id: clientId } = useSelector(createClientSelector);
  const [showDeleteContractModal, setShowDeleteContractModal] = useState(false);
  const [showUnmapWarningModal, setShowUnmapWarningModal] = useState(false);
  const [showArchiveContractModal, setShowArchiveContractModal] =
    useState(false);
  const [showStatusUpdateContractModal, setShowStatusUpdateContractModal] =
    useState(false);
  const [showCancelContractModal, setShowCancelContractModal] = useState(false);
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const isSiteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const [filteredResults, setFilteredResults] = useState<ClientDocument[]>([
    ...rows,
  ]);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const { control, reset, getValues, setValue } = useForm({
    mode: "all",
    defaultValues: {
      [CreateClientDocumentsFormKey.contractId]: "",
      [CreateClientDocumentsFormKey.documentName]: "",
      [CreateClientDocumentsFormKey.usedByClient]: "",
      [CreateClientDocumentsFormKey.documentOrigin]: "",
      [CreateClientDocumentsFormKey.exclusivityId]: "",
      [CreateClientDocumentsFormKey.geoScope]: "",
      [CreateClientDocumentsFormKey.expirationDate]: "",
      [CreateClientDocumentsFormKey.status]: "",
      [CreateClientDocumentsFormKey.tablePaginationPageNo]: 1,
    },
  });

  const tableColumnFilterWatch = useWatch({ control });

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    reset();
    resetPagination();
    setFilteredResults([...rows]);
  }, [rows]);

  useEffect(() => {
    const {
      exclusivityId,
      geoScope,
      documentName,
      contractId,
      expirationDate,
      status,
      usedByClient,
      documentOrigin,
    } = getValues();

    const filteredDocuments = [...rows].filter((item) => {
      const matchesContractId = getValueOrBlank(item?.contractId)
        .toLowerCase()
        .includes(contractId.toLowerCase());

      const matchesDocumentName = item.documentName
        .toLowerCase()
        .includes(documentName.toLowerCase());

      const matchesUsedByClient = arrayOfObjectsIncludesValue(
        usedByClient,
        (item?.usedByClient ?? []).map((client) => ({ label: client })),
        "label",
      );

      const matchesExclusivityId = exclusivityId
        ? item.exclusivityId === exclusivityId
        : true;

      const matchesGeoScope = geoScope ? item.geoScope === geoScope : true;

      const matchesExpirationDate =
        isEmpty(expirationDate) ||
        convertToUserFormat(
          item?.expirationDate ?? "",
          userCountryDateFormat,
        ) === expirationDate;

      const matchesStatus =
        isEmpty(status) ||
        fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.contractStatusList,
          item.status,
        ).toLowerCase() === status.toLowerCase();

      const matchesDocumentOrigin =
        isEmpty(documentOrigin) ||
        fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.documentOriginList,
          getValueOrBlank(item?.documentOrigin),
        ).toLowerCase() === documentOrigin.toLowerCase();

      return (
        matchesDocumentName &&
        matchesContractId &&
        matchesUsedByClient &&
        matchesDocumentOrigin &&
        matchesExclusivityId &&
        matchesGeoScope &&
        matchesExpirationDate &&
        matchesStatus
      );
    });

    setFilteredResults([...filteredDocuments]);
    resetPagination();
  }, [tableColumnFilterWatch]);

  useEffect(() => {
    if (
      isUpdateStatusContractDocumentSuccess ||
      isArchiveStatusContractDocumentSuccess
    ) {
      if (showCancelContractModal) setShowCancelContractModal(false);
      const getSuccessMessage = () => {
        if (selectedDocumentActionRef.current === ACTION_CANCEL_CONTRACT) {
          return getLabel(documentSiteCoreKeys.contractCanceledMessage);
        } else if (selectedDocumentActionRef.current === ACTION_ACTIVE) {
          return getLabel(documentSiteCoreKeys.documentMarkActiveMessage);
        } else if (selectedDocumentActionRef.current === ACTION_EXPIRED) {
          return getLabel(documentSiteCoreKeys.documentMarkExpireMessage);
        } else if (selectedDocumentActionRef.current === ACTION_ARCHIVE) {
          return getLabel(documentSiteCoreKeys.documentArchived);
        }
      };
      dispatch(
        toggleToast({
          open: true,
          message: getSuccessMessage() ?? "",
        }),
      );
      successHandler();
    }
  }, [
    isUpdateStatusContractDocumentSuccess,
    isArchiveStatusContractDocumentSuccess,
  ]);
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isUpdateStatusContractDocumentPending ||
          isArchiveStatusContractDocumentPending,
      }),
    );
  }, [
    isUpdateStatusContractDocumentPending,
    isArchiveStatusContractDocumentPending,
  ]);

  useEffect(() => {
    if (isUnmapContractDocumentSuccess) {
      displayToast(
        getLabel(documentSiteCoreKeys.documentUnmappedToastMessage),
        "success",
      );
      successHandler();
    }
  }, [isUnmapContractDocumentSuccess]);

  useEffect(() => {
    if (
      isUnmapContractDocumentError &&
      isNotEmpty(unmapContractDocumentError)
    ) {
      const isError = isErrorCode(
        unmapContractDocumentError,
        BE_ERROR_CODES.UNMAP_CLIENT_CONTRACT_DOCUMENT_IN_USE_WITH_BILLING_CONFIG,
      );
      const isErrorWithProgram = isErrorCode(
        unmapContractDocumentError,
        BE_ERROR_CODES.UNMAP_CLIENT_CONTRACT_DOCUMENT_IN_USE_WITH_PROGRAM,
      );
      if (isError || isErrorWithProgram) {
        setShowUnmapWarningModal(true);
      } else displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isUnmapContractDocumentError, unmapContractDocumentError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        unmapContractDocumentsLoader: {
          isVisible: isUnmapContractDocumentPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          unmapContractDocumentsLoader: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isUnmapContractDocumentPending]);

  const isError =
    isArchiveStatusContractDocumentError || isUpdateStatusContractDocumentError;
  const error =
    updateStatusContractDocumentError || archiveStatusContractDocumentError;

  useEffect(() => {
    if (isError && error) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(error),
        }),
      );
    }
  }, [isError, error]);

  useEffect(() => {
    if (isDeleteContractDocumentSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(documentSiteCoreKeys.documentDeleted),
        }),
      );
      successHandler();
    }
  }, [isDeleteContractDocumentSuccess]);
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteContractDocumentPending,
      }),
    );
  }, [isDeleteContractDocumentPending]);

  useEffect(() => {
    if (isDeleteContractDocumentError && deleteContractDocumentError) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(isDeleteContractDocumentError),
        }),
      );
    }
  }, [isDeleteContractDocumentError, deleteContractDocumentError]);

  const getLabel = (key: string) => {
    return fetchLabel(documentSiteCoreKeys.documentsTitle, key);
  };

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(CreateClientDocumentsFormKey.expirationDate, dateValue);
  };

  const handleModalClose = () => {
    if (showCancelContractModal) {
      setShowCancelContractModal(false);
    }
    if (showStatusUpdateContractModal) {
      setShowStatusUpdateContractModal(false);
    }
    if (showDeleteContractModal) {
      setShowDeleteContractModal(false);
    }
  };

  const handleDeleteContractDocumentClick = () => {
    const documentId = selectedDocumentRef.current?.documentId ?? "";
    if (isNotEmpty(documentId)) {
      setShowDeleteContractModal(false);
      deleteContractDocument({ documentId });
    }
  };

  const deleteContractDocumentAction = (row: ClientDocument) => {
    if (!showDeleteContractModal) {
      selectedDocumentRef.current = row;
      setShowDeleteContractModal(true);
    }
  };

  const handleEditContractDocumentClick = (
    documentId: string,
    documentType: string,
  ) => {
    onDocumentEdit?.(
      documentId,
      documentType === "contract"
        ? DOCUMENT_SITE_CORE_KEY.documentTypeContract
        : DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
    );
  };

  const handleCancelContractDocumentConfirmation = (
    cancellationReason: string,
    cancellationComments?: string,
  ) => {
    const documentId = selectedDocumentRef.current?.documentId ?? "";
    const params: UpdateStatusContractDocumentParams = {
      documentId,
      status: PARAMS_CANCEL_CONTRACT,
      cancellationReason,
      ...(cancellationComments && {
        cancellationComments,
      }),
    };
    if (isNotEmpty(documentId)) {
      setShowStatusUpdateContractModal(false);
      updateStatusContractDocument({
        ...params,
      });
    }
  };

  const handleContractDocumentAction = () => {
    const documentId = selectedDocumentRef.current?.documentId ?? "";
    if (isNotEmpty(documentId)) {
      setShowStatusUpdateContractModal(false);
      updateStatusContractDocument({
        documentId,
        status: PARAMS_UNDO_CANCEL_CONTRACT,
      });
    }
  };

  useEffect(() => {
    if (isContractClientDocumentSuccess) {
      onDocumentView &&
        onDocumentView({
          ...contractClientDocumentData,
          sowStatusId: contractClientDocumentData.status,
          documentType: DOCUMENT_SITE_CORE_KEY.documentTypeContract,
        });
    }
  }, [isContractClientDocumentSuccess]);

  const fetchDocumentData = async (documentId: string) => {
    getContractClientDocument(documentId);
  };

  const cancelContractDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateContractModal) {
      selectedDocumentRef.current = row;
      selectedDocumentActionRef.current = ACTION_CANCEL_CONTRACT;
      setShowCancelContractModal(true);
    }
  };

  const markAsActiveContractDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateContractModal) {
      selectedDocumentRef.current = row;
      selectedDocumentActionRef.current = ACTION_ACTIVE;
      setShowStatusUpdateContractModal(true);
    }
  };

  const archiveContractDocumentAction = (row: ClientDocument) => {
    if (!showArchiveContractModal) {
      selectedDocumentRef.current = row;
      selectedDocumentActionRef.current = ACTION_ARCHIVE;
      setShowArchiveContractModal(true);
    }
  };

  const markAsExpiredContractDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateContractModal) {
      selectedDocumentRef.current = row;
      selectedDocumentActionRef.current = ACTION_EXPIRED;
      setShowStatusUpdateContractModal(true);
    }
  };

  const unMapInheritedDocumentAction = (row: ClientDocument) => {
    unmapContractDocumentsMutation({
      clientId: clientId,
      contractDocumentId: row.documentId,
    });
  };

  const columns = [
    {
      id: CreateClientDocumentsFormKey.contractId,
      isSortVisible: true,
      label: getLabel(documentSiteCoreKeys.contractID),
      isEmptyValue: true,
    },
    {
      id: CreateClientDocumentsFormKey.documentName,
      label: getLabel(documentSiteCoreKeys.contractName),
      isSortVisible: true,
      className: "cell-document-name",
      render: (row: RowProps) =>
        !row.documentName ? null : (
          <MuiLink
            data-testid={COMPONENT_IDS.COMMON_TABLE_NAME}
            onClick={() => {
              fetchDocumentData(row.documentId);
            }}
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link document-name-link"
                text={row.documentName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: CreateClientDocumentsFormKey.usedByClient,
      label: getLabel(documentSiteCoreKeys.usedByClient),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => {
        if (isEmpty(row?.usedByClient)) {
          return <MuiTypography variant="h3">{EMPTY_VALUE_DASH}</MuiTypography>;
        }
        const chipOptions = row.usedByClient.map((item: string) => ({
          label: item,
          value: item,
        }));
        return (
          <MuiChipWithPopover
            data={chipOptions}
            isEditable={false}
            headerLabel={getLabel(documentSiteCoreKeys.usedByClient)}
            gridRef={gridRef}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        );
      },
    },
    {
      id: CreateClientDocumentsFormKey.status,
      label: getLabel(documentSiteCoreKeys.status),
      isSortVisible: true,
      className: "cell-status",
      render: (row: any) => {
        const statusValue = fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.contractStatusList,
          row.status,
        );
        return (
          <MuiBox className="badge-status">
            <StatusBadge status={row.status} label={statusValue} />
          </MuiBox>
        );
      },
    },
    {
      id: CreateClientDocumentsFormKey.documentOrigin,
      label: getLabel(documentSiteCoreKeys.documentOrigin),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.documentOriginList,
              row?.documentOrigin,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.geoScope,
      label: getLabel(documentSiteCoreKeys.geoScope),
      isSortVisible: true,
      className: "cell-geo-scope",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.geoScopeList,
              row.geoScope,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.expirationDate,
      label: getLabel(documentSiteCoreKeys.expirationDate),
      isSortVisible: true,
      className: "cell-expirationDate",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.expirationDate
              ? convertToUserFormat(row.expirationDate, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.exclusivityId,
      label: getLabel(documentSiteCoreKeys.exclusivityId),
      isSortVisible: true,
      className: "cell-exclusivity",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.exclusivityList,
              row.exclusivityId,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(documentSiteCoreKeys.action),
      className: "cell-action",
      render: (row: any) => (
        <MuiBox>
          {isEditable ? (
            <OptionMenu
              clientData={row}
              onClickOptionEdit={() =>
                handleEditContractDocumentClick(
                  row.documentId,
                  row.documentType,
                )
              }
              onClickOptionDelete={deleteContractDocumentAction}
              onClickCancelContract={cancelContractDocumentAction}
              onClickOptionMarkAsActive={markAsActiveContractDocumentAction}
              onClickOptionMarkAsExpired={markAsExpiredContractDocumentAction}
              onClickOptionMarkAsArchive={archiveContractDocumentAction}
              onClickOptionUnMap={unMapInheritedDocumentAction}
            />
          ) : (
            <></>
          )}
        </MuiBox>
      ),
    },
  ];

  const tableHeaderForm = [
    <FormController
      key={"contractId"}
      control={control}
      name={CreateClientDocumentsFormKey.contractId}
    >
      <MuiTextField
        key={"contractId"}
        id={`${pageId}-${CreateClientDocumentsFormKey.contractId}`}
        aria-label={commonLabels.search}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        showCloseIcon={true}
        placeholder={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        requiredARIALabel={commonARIALabel.required}
        shouldTrimSpaces={false}
      />
    </FormController>,
    <FormController
      key="documentName"
      control={control}
      name={CreateClientDocumentsFormKey.documentName}
    >
      <MuiTextField
        id={`${pageId}-${CreateClientDocumentsFormKey.documentName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        showCloseIcon
      />
    </FormController>,
    <FormController
      key={"usedByClient"}
      control={control}
      name={CreateClientDocumentsFormKey.usedByClient}
    >
      <MuiTextField
        key={"usedByClient"}
        id={`${pageId}-${CreateClientDocumentsFormKey.usedByClient}`}
        aria-label={commonLabels.search}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        showCloseIcon={true}
        placeholder={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        requiredARIALabel={commonARIALabel.required}
        shouldTrimSpaces={false}
      />
    </FormController>,
    <FormController
      key="status"
      control={control}
      name={CreateClientDocumentsFormKey.status}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.contractStatusList,
          true,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="documentOrigin"
      control={control}
      name={CreateClientDocumentsFormKey.documentOrigin}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.documentOriginList,
          true,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.documentOrigin}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="geoScope"
      control={control}
      name={CreateClientDocumentsFormKey.geoScope}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.geoScopeList,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.geoScope}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key="expirationDate"
      control={control}
      name={CreateClientDocumentsFormKey.expirationDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          onChange={handleMuiDateChange}
          clearable
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key="exclusivity"
      control={control}
      name={CreateClientDocumentsFormKey.exclusivityId}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.exclusivityList,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.exclusivityId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
  ];

  const confirmationMessage = useCallback(() => {
    if (selectedDocumentActionRef.current === ACTION_CANCEL_CONTRACT) {
      return getLabel(documentSiteCoreKeys.cancelContractActionText);
    } else if (selectedDocumentActionRef.current === ACTION_ACTIVE) {
      return getLabel(documentSiteCoreKeys.documentActiveConfirmationboxText);
    } else if (selectedDocumentActionRef.current === ACTION_EXPIRED) {
      return getLabel(documentSiteCoreKeys.documentExpiredConfirmationboxText);
    }
  }, [isSiteCoreLoading]);

  const handleCloseArchiveModal = () => {
    setShowArchiveContractModal(false);
  };

  const handleUnmapWarningModalClose = () => setShowUnmapWarningModal(false);

  const handleArchiveContractDocumentConfirmation = (
    archivalReason: string,
    archivalComments = "",
  ) => {
    const documentId = selectedDocumentRef.current?.documentId ?? "";
    const params: ArchiveStatusOtherDocumentParams = {
      documentId,
      archivalReason,
      archivalComments,
    };
    if (isNotEmpty(documentId)) {
      setShowArchiveContractModal(false);
      archiveStatusContractDocument(params);
    }
  };

  return (
    <DocumentListWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        isLastColumnSticky={isEditable}
        tableResults={[...filteredResults]}
        noDataInApi={rows.length === 0}
        noResultMessage={commonLabels.noResult}
        emptyMessage={getLabel(documentSiteCoreKeys.noRecordsAvailable)}
        priorityResults={[]}
        isFooterVisible={false}
      />
      {showDeleteContractModal && (
        <DocumentWarningConfirmationModal
          show={showDeleteContractModal}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteContractDocumentClick}
          labelContent={getLabel(documentSiteCoreKeys.deleteDocumentActionText)}
        />
      )}
      {showStatusUpdateContractModal && (
        <DocumentWarningConfirmationModal
          show={showStatusUpdateContractModal}
          onClose={handleModalClose}
          secondaryButtonTitle={commonLabels.no}
          modalTitle={commonLabels.confirmation}
          onPrimaryButtonClick={handleContractDocumentAction}
          labelContent={confirmationMessage() ?? ""}
        />
      )}
      {showCancelContractModal && (
        <CancelContractDocumentModal
          open={showCancelContractModal}
          onClose={handleModalClose}
          documentData={selectedDocumentRef.current}
          onConfirm={handleCancelContractDocumentConfirmation}
        />
      )}
      {showArchiveContractModal && (
        <ArchiveContractDocumentModal
          open={showArchiveContractModal}
          onClose={handleCloseArchiveModal}
          documentData={selectedDocumentRef.current}
          onConfirm={handleArchiveContractDocumentConfirmation}
        />
      )}
      {showUnmapWarningModal && (
        <WarningConfirmationModal
          show={showUnmapWarningModal}
          onClose={handleUnmapWarningModalClose}
          onPrimaryButtonClick={handleUnmapWarningModalClose}
          labelContent={getLabel(
            documentSiteCoreKeys.contractCannotBeUnmappedMessage,
          )}
          primaryButtonTitle={commonLabels.ok}
          showSecondaryButton={false}
        />
      )}
    </DocumentListWrapper>
  );
};
